import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import Toolbar from '@mui/material/Toolbar';
import Logo from '../../../components/Logo';
import sideBarConfig from '../sidebarConfig';
import { NavLink, matchPath, useLocation } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useAuth } from '../../../contexts/authContext';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

const drawerWidth = 240;

function NavListItem({ onDrawerClose }) {
  const auth = useAuth();
  const [expandedItems, setExpandedItems] = useState({});

  const filteredSideBarConfig = useMemo(
    () => sideBarConfig.filter((item) => (item.roles ? item.roles.includes(auth.user.roleId) : true)),
    [auth.user.roleId]
  );

  const { pathname } = useLocation();
  const match = (path) => (path ? !!matchPath({ path, end: false }, pathname) : false);

  const toggleExpand = (title) => {
    setExpandedItems((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const renderNavItems = (items) =>
    items.map(({ defaultTitle, icon, path, titleLocalId, children }) => {
      const isExpandable = !!children;

      return (
        <div key={defaultTitle}>
          <ListItemButton
            selected={match(path)}
            component={isExpandable ? 'div' : NavLink}
            to={!isExpandable ? path : undefined}
            onClick={
              isExpandable
                ? () => toggleExpand(defaultTitle)
                : onDrawerClose
            }
          >
            <ListItemIcon>{icon && icon}</ListItemIcon>
            <ListItemText
              disableTypography
              primary={<FormattedMessage id={titleLocalId} defaultMessage={defaultTitle} />}
            />
            {isExpandable && (expandedItems[defaultTitle] ? <ExpandLess /> : <ExpandMore />)}
          </ListItemButton>
          {isExpandable && (
            <Collapse in={expandedItems[defaultTitle]} timeout="auto" unmountOnExit>
              <List sx={{ pl: 4 }}>
                {renderNavItems(children)}
              </List>
            </Collapse>
          )}
        </div>
      );
    });

  return renderNavItems(filteredSideBarConfig);
}

NavListItem.propTypes = {
  onDrawerClose: PropTypes.func
};

export default function SideBar({ mobileOpen, onDrawerToggle, onDrawerClose, isMobileOrSmallDesktop }) {
  const boxSX = useMemo(() => (isMobileOrSmallDesktop ? {} : { width: { md: drawerWidth }, flexShrink: { md: 0 } }), [isMobileOrSmallDesktop]);

  const drawer = (
    <div>
      <Toolbar>
        <Logo />
      </Toolbar>
      <Divider />
      <List>
        <NavListItem onDrawerClose={onDrawerClose} />
      </List>
    </div>
  );

  return (
    <Box component="nav" sx={boxSX} aria-label="mailbox folders">
      {!isMobileOrSmallDesktop ? (
        <Drawer
          variant="permanent"
          sx={{
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      ) : (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      )}
    </Box>
  );
}

SideBar.propTypes = {
  isMobileOrSmallDesktop: PropTypes.bool,
  mobileOpen: PropTypes.bool,
  onDrawerToggle: PropTypes.func,
  onDrawerClose: PropTypes.func,
};
